import { create } from 'zustand';
import { supabase } from '../api/supabase';
import type { LoyaltySummary } from '../types/loyalty';

interface LoyaltyState {
  summary: LoyaltySummary | null;
  isLoading: boolean;
  error: string | null;
  fetchAndUpdateLoyaltySummary: (userId?: string) => Promise<void>;
}

export const useLoyaltyStore = create<LoyaltyState>((set) => ({
  summary: null,
  isLoading: false,
  error: null,

  fetchAndUpdateLoyaltySummary: async (userId?: string) => {
    set({ isLoading: true, error: null });

    try {
      // Get current user - use provided userId or fetch from auth
      let userIdToUse = userId;

      if (!userIdToUse) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          console.error('No authenticated user found');
          set({ summary: null, isLoading: false, error: 'Not authenticated' });
          return;
        }
        userIdToUse = user.id;
      }

      console.log('Fetching loyalty points for user ID:', userIdToUse);

      // Calculate points directly from loyalty_points table
      const { data: pointsData, error: pointsError } = await supabase
        .from('loyalty_points')
        .select('points_earned, points_spent')
        .eq('user_id', userIdToUse);

      if (pointsError) {
        console.error('Error fetching loyalty points:', pointsError);
        throw pointsError;
      }

      console.log('Raw loyalty points data:', pointsData);

      // Calculate total points
      const totalEarned = (pointsData || []).reduce((sum, record) => sum + (record.points_earned || 0), 0);
      const totalSpent = (pointsData || []).reduce((sum, record) => sum + (record.points_spent || 0), 0);
      const currentPoints = totalEarned - totalSpent;

      console.log('Loyalty points calculation:', { totalEarned, totalSpent, currentPoints, recordCount: pointsData?.length });

      // Get tier information (you may need to adjust this based on your tiers table structure)
      const { data: tierData } = await supabase
        .from('loyalty_tiers')
        .select('*')
        .order('minimum_points', { ascending: true });

      // Find current tier based on points
      let currentTier = tierData?.[0] || {
        id: 'default',
        name: 'Insider',
        description: 'Welcome to Zap!',
        color_hex: '#FACC15',
        minimum_points: 0
      };

      let nextTier = null;

      if (tierData) {
        for (let i = 0; i < tierData.length; i++) {
          if (currentPoints >= tierData[i].minimum_points) {
            currentTier = tierData[i];
            nextTier = tierData[i + 1] || null;
          }
        }
      }

      const validSummary = {
        total_points_earned: totalEarned,
        current_points: currentPoints,
        tier_name: currentTier.name,
        tier_description: currentTier.description,
        tier_color_hex: currentTier.color_hex,
        next_tier_name: nextTier?.name || null,
        next_tier_minimum_points: nextTier?.minimum_points || null,
        points_to_next_tier: nextTier ? (nextTier.minimum_points - currentPoints) : null
      };

      console.log('Setting loyalty summary:', validSummary);
      set({ summary: validSummary, isLoading: false });
    } catch (error) {
      console.error('Error in fetchAndUpdateLoyaltySummary:', error);
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'An unknown error occurred',
        summary: null
      });
    }
  },
}));
